// Endpoint definitions injected in transactionsApi.js; DTO → VM runs in transformResponse.
import { TAG_TYPES } from '@/lib/api/tags';
import { buildTransactionFiltersInput } from '@/lib/mappers/filters';
import {
  buildTransactionsQuery,
  processTransactionsPage,
  processTransactionAttachments,
  processTransactionFilterOptions,
} from '../mappers/transactionsMappers';

// The list is GraphQL; the attachments the row action shows stay REST.
const PATHS = {
  attachments: (module, id) => `/${module}/transactions/${id}/attachments`,
};

// The fields the Transactions table renders; `Transaction` has 30+.
const TRANSACTIONS_QUERY = `
  query Transactions($certificateType: CertificateType, $limit: Int, $page: Int, $filters: TransactionFilters, $search: TransactionSearch) {
    transactions(certificateType: $certificateType, limit: $limit, page: $page, filters: $filters, search: $search) {
      nodes {
        id
        transactionId
        associatedId
        type
        sdNo
        material { name }
        associatedSite
        terminal
        supplierCustomer { companyname }
        transactionDate
        workingUnit
        declarationUnit
        trackingUnit
        quantityWorking
        quantityDeclaration
        quantityTracking
        balanceWorking
        balanceDeclaration
        balanceTracking
        ghgEmission
        ghgEmissionSavings
        countryOfOrigin
        rawMaterialType
        feedstockCategory
        allocationPreference
        massBalancePeriod { name }
        transactedByUser { firstname lastname email }
        hasAttachments
      }
      pageInfo { total page limit }
    }
  }
`;

// The filter panel's Material and Send to / Received from choices — only what the ledger's rows carry.
const TRANSACTION_FILTER_OPTIONS_QUERY = `
  query TransactionFilterOptions($certificateType: CertificateType, $filters: TransactionFilters) {
    transactionFilterOptions(certificateType: $certificateType, filters: $filters) {
      materials { id name }
      counterparties { id name }
    }
  }
`;

export const transactionsEndpoints = (build) => ({
  getTransactions: build.query({
    // `document` routes this through GraphQL; the module scopes it as `certificateType`.
    query: (screenState) => ({
      document: TRANSACTIONS_QUERY,
      variables: buildTransactionsQuery(screenState),
    }),
    transformResponse: (data) => processTransactionsPage(data?.transactions),
    providesTags: (result) =>
      result
        ? [
            ...result.rows.map((row) => ({ type: TAG_TYPES.Transaction, id: row.id })),
            { type: TAG_TYPES.Transaction, id: 'LIST' },
          ]
        : [{ type: TAG_TYPES.Transaction, id: 'LIST' }],
  }),

  // Takes the page's fixed scope only — a picked filter here would hide its siblings.
  getTransactionFilterOptions: build.query({
    query: ({ certificateType, extraFilters }) => ({
      document: TRANSACTION_FILTER_OPTIONS_QUERY,
      variables: { certificateType, filters: buildTransactionFiltersInput({}, extraFilters) },
    }),
    transformResponse: (data) => processTransactionFilterOptions(data?.transactionFilterOptions),
    providesTags: [{ type: TAG_TYPES.Transaction, id: 'LIST' }],
  }),

  getTransactionAttachments: build.query({
    query: ({ module, id }) => ({ url: PATHS.attachments(module, id) }),
    transformResponse: (dto) => processTransactionAttachments(dto?.output?.data?.attachments),
    providesTags: (result, error, arg) => [{ type: TAG_TYPES.Transaction, id: arg.id }],
  }),
});
